// src/context/DwhProvisioningContext.tsx
//
// Tracks DWH provisioning globally so we can notify the user when
// provisioning completes — even if they navigated away from the
// Data Warehouse settings page.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "@/routing/accountRouting";
import { notifications } from "@mantine/notifications";
import { useAuth } from "@/context/AuthContext";
import { useNotificationContext } from "@/context/NotificationContext";
import { getDwhStatus } from "@/api/dataWarehouse";

interface DwhProvisioningContextType {
  /** Call after starting provisioning so polling begins immediately */
  startTracking: () => void;
  /** Current provisioning status (null = not loaded yet) */
  provisioningStatus: string | null;
}

const DwhProvisioningContext = createContext<
  DwhProvisioningContextType | undefined
>(undefined);

const POLL_INTERVAL = 5_000;

export function DwhProvisioningProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { user, activeAccount } = useAuth();
  const accountId = activeAccount?.id;
  const isAuthenticated = !!user && !!accountId;

  const navigate = useNavigate();
  const { addNotification } = useNotificationContext();

  const [provisioningStatus, setProvisioningStatus] = useState<string | null>(
    null
  );
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wasProvisioningRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const data = await getDwhStatus();
      const status = data.provisioning_status;
      setProvisioningStatus(status);

      if (status === "PROVISIONING") {
        wasProvisioningRef.current = true;
        return;
      }

      // No longer provisioning — stop polling
      stopPolling();

      // Only notify if we observed the PROVISIONING → done transition
      if (!wasProvisioningRef.current) return;
      wasProvisioningRef.current = false;

      if (status === "ACTIVE") {
        notifications.show({
          title: "Data warehouse ready",
          message:
            "Your data warehouse has been set up successfully. Click to view.",
          color: "green",
          autoClose: 8000,
          onClick: () => {
            navigate("/settings/account");
            notifications.cleanQueue();
          },
          style: { cursor: "pointer" },
        });
        addNotification({
          title: "Data warehouse ready",
          message: "Your data warehouse has been set up successfully.",
          type: "success",
          category: "system",
          action_url: "/settings/account",
          show_toast: false,
        });
      } else if (status === "FAILED") {
        notifications.show({
          title: "Data warehouse provisioning failed",
          message: "Click to view details and retry.",
          color: "red",
          autoClose: false,
          onClick: () => {
            navigate("/settings/account");
            notifications.cleanQueue();
          },
          style: { cursor: "pointer" },
        });
        addNotification({
          title: "Data warehouse provisioning failed",
          message: "Click to view details and retry.",
          type: "error",
          category: "system",
          action_url: "/settings/account",
          show_toast: false,
        });
      }
    } catch {
      // Silent — polling failure is non-critical
    }
  }, [stopPolling, navigate, addNotification]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(poll, POLL_INTERVAL);
  }, [poll]);

  // Called by DataWarehouse page after provisioning starts
  const startTracking = useCallback(() => {
    wasProvisioningRef.current = true;
    setProvisioningStatus("PROVISIONING");
    stopPolling();
    startPolling();
  }, [stopPolling, startPolling]);

  // On auth/account change: check if provisioning is in progress
  useEffect(() => {
    if (!isAuthenticated) {
      stopPolling();
      wasProvisioningRef.current = false;
      setProvisioningStatus(null);
      return;
    }

    getDwhStatus()
      .then((data) => {
        setProvisioningStatus(data.provisioning_status);
        if (data.provisioning_status === "PROVISIONING") {
          wasProvisioningRef.current = true;
          startPolling();
        }
      })
      .catch(() => {});

    return stopPolling;
  }, [isAuthenticated, accountId, startPolling, stopPolling]);

  // Pause polling when tab is hidden, resume when visible
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else if (wasProvisioningRef.current) {
        poll();
        startPolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [isAuthenticated, poll, startPolling, stopPolling]);

  return (
    <DwhProvisioningContext.Provider
      value={{ startTracking, provisioningStatus }}
    >
      {children}
    </DwhProvisioningContext.Provider>
  );
}

export function useDwhProvisioning(): DwhProvisioningContextType {
  const context = useContext(DwhProvisioningContext);
  if (!context) {
    throw new Error(
      "useDwhProvisioning must be used within DwhProvisioningProvider"
    );
  }
  return context;
}
